import React from 'react';
import type { NumberCellProps } from './types';

/**
 * NumberCell - Renders numeric values
 * 
 * Supports three formatting modes:
 * 1. Simple: { decimals: 2 }
 * 2. Extended: { decimals, thousandsSeparator, prefix, suffix }
 * 3. Presets: { format: 'currency' | 'percentage' | 'integer' }
 * 
 * Renders as plain text with tabular numbers for alignment
 */
export const NumberCell: React.FC<NumberCellProps> = ({ value, config, row }) => {
  // Handle null/undefined values
  if (value === null || value === undefined || value === '') {
    return <span className="table-cell-text">—</span>;
  }

  // Convert to number
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  
  if (isNaN(numValue)) {
    return <span className="table-cell-text">Invalid Number</span>;
  }

  let displayValue: string;

  // Preset format mode
  if (config?.format) {
    switch (config.format) {
      case 'currency':
        displayValue = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numValue);
        break;
        
      case 'percentage':
        displayValue = new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(numValue / 100); // Assuming value is 0-100
        break;
        
      case 'integer':
        displayValue = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(Math.round(numValue));
        break;
        
      case 'decimal':
      default:
        displayValue = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: config.decimals ?? 2,
          maximumFractionDigits: config.decimals ?? 2
        }).format(numValue);
        break;
    }
  } else {
    // Simple or extended mode
    const decimals = config?.decimals ?? 0;
    
    if (config?.thousandsSeparator) {
      // Extended mode with thousand separators
      displayValue = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(numValue);
    } else {
      // Simple mode without separators
      displayValue = numValue.toFixed(decimals);
    }
    
    // Add prefix/suffix if provided
    if (config?.prefix) {
      displayValue = config.prefix + displayValue;
    }
    if (config?.suffix) {
      displayValue = displayValue + config.suffix;
    }
  }

  // Add period/rate if provided
  if (config?.period) {
    displayValue = `${displayValue} / ${config.period}`;
  }

  // Determine text color and weight (matching TextCell)
  const colorMode = config?.color || 'default';
  const textColor = colorMode === 'strong' 
    ? 'var(--core-text-primary)'   // strong = primary (darker)
    : 'var(--core-text-secondary)'; // default = secondary (lighter)
  const fontWeight = config?.fontWeight || 400;  // Default: 400 (normal)

  return (
    <span 
      className={`table-cell-text ${config?.className || ''}`}
      style={{ 
        fontSize: '0.875rem',
        color: textColor,
        fontWeight,
        fontVariantNumeric: config?.tabularNums === true ? 'tabular-nums' : undefined 
      }}
      title={displayValue}
    >
      {displayValue}
    </span>
  );
};

